import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Hero } from "@/components/home/Hero";
import { CompanionCard } from "@/components/home/CompanionCard";
import { FeaturesSection } from "@/components/home/FeaturesSection";
import { ChatWidget } from "@/components/chat/ChatWidget";
import { SEOHead, schemas } from "@/components/seo/SEOHead";
import { CompanionFilters, MobileFilterButton, applyFilters, INITIAL_FILTERS, type FilterState } from "@/components/home/CompanionFilters";
import { MapPin, Heart, Users, ShieldCheck, X } from "lucide-react";
import type { Companion } from "@shared/schema";

const quickFilters = ["All", "New", "Verified", "Available", "Duo"];

const homeFaqs = [
  { question: "How do I book an escort in Manchester?", answer: "Simply browse our verified companion profiles, choose your preferred escort, and contact us via WhatsApp, phone, or our online chat. We're available 24/7 and can often arrange same-day bookings for both incall and outcall appointments." },
  { question: "Do you offer incall and outcall services?", answer: "Yes. We offer incall appointments at our discreet city centre locations (M1/M2) and outcall services to hotels, homes, and private addresses across all of Greater Manchester and Cheshire." },
  { question: "Are your escort photos verified?", answer: "Absolutely. Every companion at Luxury Escorts Manchester is selfie-verified. The photos on our website are confirmed authentic, so the person you see is exactly who you'll meet." },
  { question: "What areas of Manchester do you cover?", answer: "We cover all of Greater Manchester including the city centre, Salford, Stockport, Bolton, Altrincham, Didsbury, Trafford, and 30+ more areas. We also serve North Cheshire including Wilmslow, Alderley Edge, and Knutsford." },
  { question: "What is a GFE escort?", answer: "GFE stands for Girlfriend Experience \u2014 a style of companionship that emphasises warmth, genuine conversation, and a natural, relaxed interaction. Our GFE escorts are selected for their emotional intelligence and ability to create authentic connections." },
  { question: "Are you available 24/7?", answer: "Yes, Luxury Escorts Manchester operates 24 hours a day, 7 days a week, 365 days a year. Contact us anytime to check availability and arrange your booking." },
];

export default function HomePage() {
  const [activeQuickFilter, setActiveQuickFilter] = useState("All");
  const [advancedFilters, setAdvancedFilters] = useState<FilterState>(INITIAL_FILTERS);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const { data: companions = [], isLoading } = useQuery<Companion[]>({
    queryKey: ["/api/companions"],
  });

  // Apply quick filter first, then advanced filters
  const quickFiltered = companions.filter((c) => {
    if (activeQuickFilter === "All") return true;
    if (activeQuickFilter === "Available") return c.availability === "Available Now";
    if (activeQuickFilter === "New") return c.tags?.includes("New");
    if (activeQuickFilter === "Verified") return c.tags?.includes("Verified");
    if (activeQuickFilter === "Duo") return c.services?.some(s => s.toLowerCase().includes("bi duo") || s.toLowerCase() === "duo");
    return true;
  });
  const filteredCompanions = applyFilters(quickFiltered, advancedFilters);

  const activeFilterCount =
    advancedFilters.services.length +
    advancedFilters.hairColor.length +
    advancedFilters.breastSize.length +
    advancedFilters.dressSize.length +
    advancedFilters.nationality.length +
    advancedFilters.bodyType.length +
    (advancedFilters.ageRange ? 1 : 0) +
    (advancedFilters.priceRange ? 1 : 0) +
    advancedFilters.availability.length;

  return (
    <div
      className="min-h-screen bg-[#0F0F0F] font-sans selection:bg-[#D4AF37] selection:text-black"
      data-testid="page-home"
    >
      <SEOHead
        canonical="/"
        jsonLd={[
          schemas.localBusiness(),
          schemas.organization(),
          schemas.faqPage(homeFaqs),
        ]}
      />

      <Header />

      <main>
        <Hero />

        {/* Available Companions Section */}
        <section className="py-24 px-6 max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
            <div>
              <h2 className="text-3xl font-light text-white mb-2 uppercase tracking-widest">
                Available{" "}
                <span className="font-semibold text-[#D4AF37]">Companions</span>
              </h2>
              <p className="text-gray-400 text-sm max-w-md font-light">
                Browse our curated selection of verified Manchester escorts currently available for
                incall and outcall companionship across Greater Manchester.
              </p>
            </div>
            <div className="flex gap-3 flex-wrap items-center">
              <MobileFilterButton
                activeCount={activeFilterCount}
                onClick={() => setMobileFiltersOpen(true)}
              />
              {quickFilters.map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setActiveQuickFilter(f)}
                  data-testid={`button-filter-${f.toLowerCase()}`}
                  className={`px-4 py-1.5 rounded-full text-[10px] uppercase tracking-widest border transition-all ${
                    f === activeQuickFilter
                      ? "border-[#D4AF37] text-[#D4AF37]"
                      : "border-gray-700 text-gray-400 hover:border-gray-500"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>

          <div className="flex gap-8">
            {/* Desktop Filter Sidebar */}
            <aside className="hidden lg:block w-64 flex-shrink-0 sticky top-28 self-start max-h-[calc(100vh-8rem)] overflow-y-auto scrollbar-thin z-30 relative">
              <CompanionFilters
                filters={advancedFilters}
                onChange={setAdvancedFilters}
                resultCount={filteredCompanions.length}
              />
            </aside>

            {/* Mobile Filter Drawer */}
            {mobileFiltersOpen && (
              <div className="lg:hidden fixed inset-0 z-50 flex">
                <div
                  className="absolute inset-0 bg-black/60"
                  onClick={() => setMobileFiltersOpen(false)}
                />
                <div className="relative ml-auto w-80 max-w-[85vw] h-full bg-[#0F0F0F] overflow-y-auto">
                  <div className="sticky top-0 z-10 bg-[#0F0F0F] px-4 py-3 border-b border-gray-800 flex items-center justify-between">
                    <span className="text-white text-sm font-bold uppercase tracking-widest">Filters</span>
                    <button type="button" title="Close filters" onClick={() => setMobileFiltersOpen(false)} className="text-gray-400 hover:text-white">
                      <X size={20} />
                    </button>
                  </div>
                  <div className="p-4">
                    <CompanionFilters
                      filters={advancedFilters}
                      onChange={setAdvancedFilters}
                      resultCount={filteredCompanions.length}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Companions Grid */}
            <div className="flex-1 min-w-0">
              {isLoading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-8">
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="bg-[#1A1A1A] rounded-xl overflow-hidden border border-gray-800"
                    >
                      <div className="aspect-[3/4] bg-gray-800 animate-pulse" />
                    </div>
                  ))}
                </div>
              ) : filteredCompanions.length === 0 ? (
                <div className="text-center py-20">
                  <p className="text-gray-500 text-lg">No companions match your filters.</p>
                  <p className="text-gray-600 text-sm mt-2">
                    Try adjusting your filters or{" "}
                    <button
                      type="button"
                      onClick={() => {
                        setAdvancedFilters(INITIAL_FILTERS);
                        setActiveQuickFilter("All");
                      }}
                      className="text-[#D4AF37] underline decoration-[#D4AF37]/40 hover:decoration-[#D4AF37]"
                    >
                      clear all filters
                    </button>.
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-8">
                  {filteredCompanions.map((companion) => (
                    <CompanionCard key={companion.id} companion={companion} />
                  ))}
                </div>
              )}
            </div>
          </div>
        </section>

        <FeaturesSection />

        {/* About / SEO Content Section */}
        <section className="py-24 px-6 bg-[#0A0A0A]">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-light text-white mb-8 uppercase tracking-widest text-center">
              Manchester's Premier{" "}
              <span className="text-[#D4AF37] font-semibold">Escort Agency</span>
            </h2>
            <div className="text-gray-400 text-sm leading-relaxed space-y-4 mb-16">
              <p>
                Luxury Escorts Manchester is the leading escort agency in Greater Manchester, providing verified,
                premium companions for discerning clients since our establishment. We combine modern technology
                with traditional values of discretion, quality, and exceptional personal service to deliver
                an escort experience that stands apart from the competition.
              </p>
              <p>
                Every companion at Luxury Escorts Manchester undergoes our rigorous selfie verification process,
                ensuring the woman you see on our website is exactly who arrives for your appointment. Unlike
                many agencies that rely on outdated or heavily edited photos, we prioritise authenticity and
                transparency. Our companions' profiles feature genuine selfies alongside professional photos,
                giving you complete confidence in your booking.
              </p>
              <p>
                We offer both <Link href="/services/incall" className="text-[#D4AF37] underline decoration-[#D4AF37]/40 hover:decoration-[#D4AF37]">incall appointments</Link> at
                our elegant, discreet city centre locations and <Link href="/services/outcall" className="text-[#D4AF37] underline decoration-[#D4AF37]/40 hover:decoration-[#D4AF37]">outcall services</Link> to
                hotels, homes, and private addresses across all of Greater Manchester and into Cheshire.
                Our multi-channel booking system allows you to arrange your companion via WhatsApp, phone,
                or our website chat \u2014 whichever you find most convenient and private.
              </p>
              <p>
                Whether you're seeking a <Link href="/categories/gfe-escorts" className="text-[#D4AF37] underline decoration-[#D4AF37]/40 hover:decoration-[#D4AF37]">girlfriend experience (GFE)</Link>,
                a glamorous <Link href="/categories/duo-escorts" className="text-[#D4AF37] underline decoration-[#D4AF37]/40 hover:decoration-[#D4AF37]">duo escort</Link> pairing,
                an <Link href="/categories/elite-escorts" className="text-[#D4AF37] underline decoration-[#D4AF37]/40 hover:decoration-[#D4AF37]">elite companion</Link> for a
                corporate event, or simply beautiful company for an evening in Manchester, our agency delivers.
                With transparent pricing displayed on every profile, 24/7 availability, and a commitment to
                your privacy, Luxury Escorts Manchester is the smart choice for premium escort services in the North West.
              </p>
            </div>

            {/* Service Highlights */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
              {[
                { icon: ShieldCheck, title: "Selfie Verified", desc: "Every companion confirmed authentic with selfie photos" },
                { icon: MapPin, title: "35+ Locations", desc: "Covering all of Greater Manchester and Cheshire" },
                { icon: Heart, title: "GFE Specialists", desc: "Warm, genuine girlfriend experience companions" },
                { icon: Users, title: "Duo Available", desc: "Premium duo pairings for extraordinary experiences" },
              ].map((item, idx) => (
                <div key={idx} className="text-center p-6 rounded-2xl bg-[#141414] border border-gray-800">
                  <item.icon className="text-[#D4AF37] mx-auto mb-3" size={28} />
                  <h3 className="text-white text-sm font-semibold uppercase tracking-widest mb-2">{item.title}</h3>
                  <p className="text-gray-400 text-xs">{item.desc}</p>
                </div>
              ))}
            </div>

            {/* Escorts by Category */}
            <h2 className="text-2xl font-light text-white mb-6 uppercase tracking-widest">
              Escorts by <span className="text-[#D4AF37] font-semibold">Category</span>
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-16">
              {[
                { name: "GFE Escorts", slug: "gfe-escorts" },
                { name: "Busty Escorts", slug: "busty-escorts" },
                { name: "Blonde Escorts", slug: "blonde-escorts" },
                { name: "Brunette Escorts", slug: "brunette-escorts" },
                { name: "Mature Escorts", slug: "mature-escorts" },
                { name: "Duo Escorts", slug: "duo-escorts" },
                { name: "Elite Escorts", slug: "elite-escorts" },
                { name: "British Escorts", slug: "british-escorts" },
                { name: "Party Girl Escorts", slug: "party-girl-escorts" },
                { name: "Couples Escorts", slug: "couples-escorts" },
              ].map((cat) => (
                <Link
                  key={cat.slug}
                  href={`/categories/${cat.slug}`}
                  className="px-4 py-3 bg-[#141414] rounded-xl border border-gray-800 hover:border-[#D4AF37]/40 transition-all text-xs text-gray-400 hover:text-[#D4AF37] text-center"
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {/* Escorts by Location */}
            <h2 className="text-2xl font-light text-white mb-6 uppercase tracking-widest">
              Escorts by <span className="text-[#D4AF37] font-semibold">Location</span> in Greater Manchester
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-8">
              {[
                { name: "City Centre", slug: "manchester-city-centre" },
                { name: "Salford", slug: "salford" },
                { name: "Stockport", slug: "stockport" },
                { name: "Bolton", slug: "bolton" },
                { name: "Altrincham", slug: "altrincham" },
                { name: "Didsbury", slug: "didsbury" },
                { name: "Chorlton", slug: "chorlton" },
                { name: "Trafford", slug: "trafford" },
                { name: "Deansgate", slug: "deansgate" },
                { name: "Northern Quarter", slug: "northern-quarter" },
                { name: "Cheshire", slug: "cheshire" },
                { name: "Oldham", slug: "oldham" },
              ].map((loc) => (
                <Link
                  key={loc.slug}
                  href={`/location/${loc.slug}`}
                  className="px-3 py-2 bg-[#141414] rounded-lg border border-gray-800 hover:border-[#D4AF37]/40 transition-all text-xs text-gray-400 hover:text-[#D4AF37] text-center"
                >
                  {loc.name}
                </Link>
              ))}
            </div>
            <div className="text-center mb-16">
              <Link href="/locations" className="text-[#D4AF37] text-[10px] uppercase tracking-widest font-bold hover:underline">
                View All 35+ Locations &rarr;
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-24 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-light text-white mb-12 uppercase tracking-widest text-center">
              Frequently Asked{" "}
              <span className="text-[#D4AF37] font-semibold">Questions</span>
            </h2>
            <div className="space-y-6">
              {homeFaqs.map((faq, i) => (
                <div key={i} className="bg-[#141414] rounded-xl p-6 border border-gray-800">
                  <h3 className="text-white font-medium mb-2">{faq.question}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{faq.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Blog / Guides Teaser */}
        <section className="py-16 px-6 bg-[#0A0A0A] border-t border-gray-900">
          <div className="max-w-5xl mx-auto text-center">
            <h2 className="text-2xl font-light text-white mb-8 uppercase tracking-widest">
              Guides & <span className="text-[#D4AF37] font-semibold">Resources</span>
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <Link href="/blog/complete-guide-booking-escorts-manchester" className="bg-[#141414] rounded-xl p-6 border border-gray-800 hover:border-[#D4AF37]/40 transition-all text-left group">
                <span className="text-[#D4AF37] text-[10px] uppercase tracking-widest font-bold">Guide</span>
                <h3 className="text-white text-sm font-medium mt-2 group-hover:text-[#D4AF37] transition-colors">
                  The Complete Guide to Booking Escorts in Manchester
                </h3>
              </Link>
              <Link href="/blog/escort-friendly-hotels-manchester" className="bg-[#141414] rounded-xl p-6 border border-gray-800 hover:border-[#D4AF37]/40 transition-all text-left group">
                <span className="text-[#D4AF37] text-[10px] uppercase tracking-widest font-bold">Hotels</span>
                <h3 className="text-white text-sm font-medium mt-2 group-hover:text-[#D4AF37] transition-colors">
                  Escort Friendly Hotels in Manchester City Centre
                </h3>
              </Link>
              <Link href="/blog/what-is-gfe-escort-guide" className="bg-[#141414] rounded-xl p-6 border border-gray-800 hover:border-[#D4AF37]/40 transition-all text-left group">
                <span className="text-[#D4AF37] text-[10px] uppercase tracking-widest font-bold">Explained</span>
                <h3 className="text-white text-sm font-medium mt-2 group-hover:text-[#D4AF37] transition-colors">
                  What Is a GFE Escort? Your Complete Guide
                </h3>
              </Link>
            </div>
            <Link href="/blog" className="text-[#D4AF37] text-[10px] uppercase tracking-widest font-bold hover:underline">
              Read All Guides &rarr;
            </Link>
          </div>
        </section>
      </main>

      <Footer />
      <ChatWidget />
    </div>
  );
}
